/*
 * / — trang chào của Simba. Đã đăng nhập → chuyển thẳng sang /my-courses.
 * Chưa đăng nhập: lời chào + nút đăng nhập Gmail + danh sách khoá công khai.
 */
import { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { motion, useReducedMotion } from "framer-motion"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Skeleton } from "@/components/ui/skeleton"
import { Icon, type IconName } from "@/components/icon"
import { LoadingState, Page, SectionTitle, usePageTitle } from "@/components/page"
import { useSession } from "@/app/session"
import { api, loginUrl } from "@/lib/api"

const PERKS: { icon: IconName; title: string; text: string }[] = [
  { icon: "book-open", title: "Học đúng lớp", text: "Bài học, buổi học và tài liệu của lớp con ở một chỗ." },
  { icon: "sparkles", title: "Làm sản phẩm thật", text: "Mỗi khoá là một hành trình tự tay làm ra sản phẩm." },
  { icon: "trophy", title: "Thấy mình tiến bộ", text: "Điểm kinh nghiệm, huy hiệu và chứng nhận khi hoàn thành." },
]

export default function HomePage() {
  usePageTitle(
    undefined,
    "Simba là không gian học tập của học viên Sư Tử Con: vào lớp, học bài, làm sản phẩm và theo dõi tiến bộ của con.",
    { noIndex: false },
  )
  const { loading, user } = useSession()
  const reduce = useReducedMotion()
  const [courses, setCourses] = useState<any[] | null>(null)

  useEffect(() => {
    if (user) location.replace("/my-courses")
  }, [user])
  useEffect(() => {
    api("/api/public/courses")
      .then((d) => setCourses(d.courses || []))
      .catch(() => setCourses([]))
  }, [])

  if (loading) return <LoadingState label="Đang kiểm tra đăng nhập…" />
  if (user) return <LoadingState label="Đang mở khóa học…" />

  return (
    <Page>
      <section className="grid items-center gap-8 md:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)]">
        <div className="flex flex-col items-start gap-5">
          <span className="rounded-full bg-soft px-3 py-1 text-sm font-semibold text-accent-foreground">Simba Learner Space</span>
          <h1 className="font-heading text-4xl leading-tight font-bold text-ink sm:text-5xl">
            Nơi trú ẩn của <em className="text-orange not-italic">học viên</em> Sư Tử Con
          </h1>
          <p className="max-w-xl text-lg text-muted-foreground">
            Đăng nhập bằng Gmail để vào lớp, học bài, nộp sản phẩm và xem mình đã tiến bộ thế nào. Bí mật nhé!
          </p>
          <Button size="lg" nativeButton={false} render={<a href={loginUrl(`${location.origin}/my-courses`)} />}>
            <Icon name="user" /> Đăng nhập bằng Gmail
          </Button>
        </div>
        <motion.img
          src="/brand/sutucon/mascots-3d/lion-wave.webp"
          alt="Sư Tử Con vẫy tay chào"
          width="480"
          height="480"
          className="mx-auto w-56 sm:w-72 md:w-full md:max-w-sm"
          animate={reduce ? undefined : { y: [0, -8, 0] }}
          transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
        />
      </section>

      <ul className="grid gap-4 sm:grid-cols-3">
        {PERKS.map((p) => (
          <li key={p.title}>
            <Card size="sm" className="h-full">
              <CardContent className="flex flex-col gap-3">
                <span className="grid size-11 place-items-center rounded-2xl bg-soft">
                  <Icon name={p.icon} className="size-6" />
                </span>
                <span className="font-heading text-lg font-semibold text-ink">{p.title}</span>
                <span className="text-sm text-muted-foreground">{p.text}</span>
              </CardContent>
            </Card>
          </li>
        ))}
      </ul>

      <section className="flex flex-col gap-4">
        <SectionTitle title="Các khóa học" description="Xem trước nội dung từng khóa." />
        {!courses ? (
          <div className="grid gap-3 sm:grid-cols-2">
            {[0, 1].map((i) => (
              <Skeleton key={i} className="h-20 rounded-[20px]" />
            ))}
          </div>
        ) : courses.length ? (
          <ul className="grid gap-3 sm:grid-cols-2">
            {courses.map((c) => (
              <li key={c.id || c.slug}>
                <Link to={`/courses/${encodeURIComponent(c.slug)}`} className="group block">
                  <Card size="sm" className="transition-[transform,box-shadow] group-hover:-translate-y-0.5 group-hover:shadow-card-hover">
                    <CardContent className="flex items-center gap-4">
                      <span className="grid size-12 shrink-0 place-items-center rounded-2xl bg-soft">
                        <Icon name="book" className="size-6" />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate font-heading text-lg font-semibold text-ink">{c.name}</span>
                        <span className="line-clamp-1 text-sm text-muted-foreground">{c.description || "Xem khóa học"}</span>
                      </span>
                      <Icon name="arrow-right" className="size-5 shrink-0 transition-transform group-hover:translate-x-1" />
                    </CardContent>
                  </Card>
                </Link>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-muted-foreground">Các khóa học sẽ sớm xuất hiện ở đây.</p>
        )}
      </section>
    </Page>
  )
}
